import React, { useState } from 'react';
import SignaturePad from './SignaturePad';
import Icon from '../Icon';
import api from '../../utils/api';

export default function EnrollmentDecisionForm({ employee, client, content, savings, onComplete, onCancel }) {
  const [decision, setDecision] = useState('');
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const enrollmentContent = content.enrollmentForm || {};
  const canSubmit = decision && consent && signature;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setError('Please complete all required fields');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          decision,
          consent,
          signature,
          comments: comments || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        onComplete(result.status);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit enrollment. Please try again.');
      }
    } catch (err) {
      console.error('Enrollment submission error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div
        style={{
          background: '#fff',
          border: '1px solid rgb(226, 232, 240)',
          borderRadius: 16,
          padding: 40
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", margin: 0, marginBottom: 8, color: 'rgb(15, 23, 42)' }}>
            Complete Your Enrollment Decision
          </h1>
          <p style={{ fontSize: 14, color: 'rgb(71, 85, 105)', margin: 0 }}>
            Please review the information and make your decision below.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Decision Selection */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'rgb(15, 23, 42)', display: 'block', marginBottom: 16 }}>
              Your Decision *
            </label>

            {/* Opt In */}
            <label
              data-testid="enrollment-decision-opt-in-container"
              style={{
                display: 'block',
                padding: 20,
                border: `2px solid ${decision === 'opt_in' ? 'rgb(14, 116, 144)' : 'rgb(226, 232, 240)'}`,
                borderRadius: 12,
                marginBottom: 12,
                cursor: 'pointer',
                background: decision === 'opt_in' ? 'rgb(240, 253, 250)' : '#fff',
                transition: 'border-color 0.15s, background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (decision !== 'opt_in') {
                  e.currentTarget.style.borderColor = 'rgb(100, 116, 139)';
                }
              }}
              onMouseLeave={(e) => {
                if (decision !== 'opt_in') {
                  e.currentTarget.style.borderColor = 'rgb(226, 232, 240)';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <input
                  id="enrollment-decision-opt-in"
                  type="radio"
                  name="decision"
                  value="opt_in"
                  data-testid="enrollment-decision-opt-in-radio"
                  checked={decision === 'opt_in'}
                  onChange={(e) => setDecision(e.target.value)}
                  style={{ marginTop: 2, accentColor: 'rgb(14, 116, 144)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'rgb(15, 23, 42)' }}>
                    {enrollmentContent.optInLabel || 'Yes, I want to enroll and save on taxes'}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgb(71, 85, 105)', lineHeight: 1.5 }}>
                    {enrollmentContent.optInDescription || 'I understand that my health insurance premiums will be paid with pre-tax dollars, reducing my taxable income.'}
                  </div>
                </div>
              </div>
            </label>

            {/* Decline */}
            <label
              data-testid="enrollment-decision-decline-container"
              style={{
                display: 'block',
                padding: 20,
                border: `2px solid ${decision === 'decline' ? 'rgb(220, 38, 38)' : 'rgb(226, 232, 240)'}`,
                borderRadius: 12,
                cursor: 'pointer',
                background: decision === 'decline' ? 'rgb(254, 242, 242)' : '#fff',
                transition: 'border-color 0.15s, background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (decision !== 'decline') {
                  e.currentTarget.style.borderColor = 'rgb(100, 116, 139)';
                }
              }}
              onMouseLeave={(e) => {
                if (decision !== 'decline') {
                  e.currentTarget.style.borderColor = 'rgb(226, 232, 240)';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <input
                  id="enrollment-decision-decline"
                  type="radio"
                  name="decision"
                  value="decline"
                  data-testid="enrollment-decision-decline-radio"
                  checked={decision === 'decline'}
                  onChange={(e) => setDecision(e.target.value)}
                  style={{ marginTop: 2, accentColor: 'rgb(220, 38, 38)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'rgb(15, 23, 42)' }}>
                    {enrollmentContent.declineLabel || 'No, I prefer to keep my current setup'}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgb(71, 85, 105)', lineHeight: 1.5 }}>
                    {enrollmentContent.declineDescription || 'I understand that I am declining to participate. My payroll will remain unchanged.'}
                  </div>
                </div>
              </div>
            </label>
          </div>

          {/* Optional Comments */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'rgb(15, 23, 42)', display: 'block', marginBottom: 8 }}>
              Comments or Questions (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments or questions for your HR team..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 14,
                border: '2px solid rgb(226, 232, 240)',
                borderRadius: 8,
                outline: 'none',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'border-color 0.15s'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgb(14, 116, 144)'}
              onBlur={(e) => e.target.style.borderColor = 'rgb(226, 232, 240)'}
            />
          </div>

          {/* Consent */}
          <div style={{ marginBottom: 32 }}>
            <label
              data-testid="enrollment-consent-container"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 16,
                background: 'rgb(241, 245, 249)',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              <input
                id="enrollment-consent-checkbox"
                name="consent"
                type="checkbox"
                data-testid="enrollment-consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'rgb(14, 116, 144)', width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13, color: 'rgb(71, 85, 105)', lineHeight: 1.5 }}>
                {enrollmentContent.consentText || 'I have read and understand the program details. I confirm that my decision is voluntary.'}
              </span>
            </label>
          </div>

          {/* Signature */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: 'rgb(15, 23, 42)', display: 'block', marginBottom: 8 }}>
              Signature *
            </label>
            <p style={{ fontSize: 13, color: 'rgb(71, 85, 105)', marginBottom: 12, lineHeight: 1.5 }}>
              {enrollmentContent.signatureInstructions || 'Sign below to confirm your enrollment decision. You can type your name or draw your signature.'}
            </p>
            <SignaturePad onSignatureChange={setSignature} />
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: 12,
                background: 'rgb(254, 226, 226)',
                border: '1px solid rgb(254, 202, 202)',
                borderRadius: 8,
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <Icon name="alert-circle-outline" style={{ fontSize: 18, color: 'rgb(220, 38, 38)' }} />
              <span style={{ fontSize: 13, color: 'rgb(220, 38, 38)' }}>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={{
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                color: 'rgb(71, 85, 105)',
                background: 'transparent',
                border: '1px solid rgb(226, 232, 240)',
                borderRadius: 8,
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s, border-color 0.15s',
                opacity: submitting ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  e.target.style.background = 'rgb(241, 245, 249)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              data-testid="enrollment-submit-button"
              disabled={!canSubmit || submitting}
              style={{
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                color: '#fff',
                background: canSubmit && !submitting ? 'rgb(14, 116, 144)' : 'rgb(203, 213, 225)',
                border: 'none',
                borderRadius: 8,
                cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.15s, box-shadow 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onMouseEnter={(e) => {
                if (canSubmit && !submitting) {
                  e.target.style.background = 'rgb(11, 93, 115)';
                  e.target.style.boxShadow = '0 10px 30px rgba(2, 6, 23, 0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (canSubmit && !submitting) {
                  e.target.style.background = 'rgb(14, 116, 144)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {submitting ? (
                <>
                  <Icon name="hourglass-outline" style={{ fontSize: 16 }} />
                  Submitting...
                </>
              ) : (
                <>
                  <Icon name="checkmark-circle" style={{ fontSize: 16 }} />
                  {enrollmentContent.submitLabel || 'Submit Enrollment Decision'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
